// docaligner-inference.js - DocAligner ONNX 모델 추론

import { getExecutionProvider } from './scan-settings.js';

const MODEL_PATH = '/lib/docaligner.onnx';
const INPUT_SIZE = 256;
const OUTPUT_SIZE = 128;
const CONFIDENCE_THRESHOLD = 0.3;

// 싱글톤 세션
let session = null;
let loadingPromise = null;
let currentProvider = null;  // 현재 사용 중인 프로바이더

// 실시간 추론용 재사용 버퍼 (메모리 할당 최소화)
let realtimeCanvas = null;
let realtimeCtx = null;
let reusableInputBuffer = null;  // Float32Array 재사용

// 추론 시간 통계
let inferenceStats = {
  count: 0,
  totalTime: 0,
  avgTime: 0
};

/**
 * 실행 프로바이더 목록 반환
 * @param {'auto' | 'wasm'} provider
 * @returns {string[]}
 */
function getExecutionProviders(provider) {
  if (provider === 'wasm') {
    return ['wasm'];
  }
  // auto: WebGPU > WebGL > WASM 순서
  return ['webgpu', 'webgl', 'wasm'];
}

/**
 * DocAligner 모델 로드 (싱글톤)
 * @param {'auto' | 'wasm'} [provider] - 실행 프로바이더 (기본: 설정값)
 * @returns {Promise<void>}
 */
export async function loadDocAlignerModel(provider) {
  const targetProvider = provider || getExecutionProvider();

  if (session && currentProvider === targetProvider) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    try {
      // ONNX Runtime Web 로드 확인
      if (typeof ort === 'undefined') {
        throw new Error('ONNX Runtime Web이 로드되지 않았습니다');
      }

      // 기존 세션 해제
      if (session) {
        await session.release();
        session = null;
      }

      const providers = getExecutionProviders(targetProvider);
      const options = {
        executionProviders: providers,
        graphOptimizationLevel: 'all'
      };

      session = await ort.InferenceSession.create(MODEL_PATH, options);
      currentProvider = targetProvider;

      // 통계 초기화
      inferenceStats = { count: 0, totalTime: 0, avgTime: 0 };

      console.log(`DocAligner model loaded successfully (provider: ${targetProvider})`);
    } catch (e) {
      console.error('Failed to load DocAligner model:', e);
      session = null;
      currentProvider = null;
      throw e;
    } finally {
      loadingPromise = null;
    }
  })();

  return loadingPromise;
}

/**
 * 다른 프로바이더로 모델 재로드
 * @param {'auto' | 'wasm'} provider
 * @returns {Promise<void>}
 */
export async function reloadDocAlignerModel(provider) {
  currentProvider = null;  // 강제 재로드
  return loadDocAlignerModel(provider);
}

/**
 * 현재 실행 프로바이더 반환
 * @returns {'auto' | 'wasm' | null}
 */
export function getCurrentProvider() {
  return currentProvider;
}

/**
 * 추론 시간 통계 반환
 * @returns {{ count: number, avgTime: number }}
 */
export function getInferenceStats() {
  return { count: inferenceStats.count, avgTime: Math.round(inferenceStats.avgTime) };
}

/**
 * 추론 시간 기록
 * @param {number} time
 */
function recordInferenceTime(time) {
  inferenceStats.count++;
  inferenceStats.totalTime += time;
  inferenceStats.avgTime = inferenceStats.totalTime / inferenceStats.count;
}

// 유예 해제 타이머
let releaseTimer = null;
const RELEASE_DELAY = 30000;  // 30초 후 해제

/**
 * DocAligner 모델 준비 여부
 * @returns {boolean}
 */
export function isDocAlignerReady() {
  return session !== null;
}

/**
 * ONNX 세션 및 GPU 메모리 해제
 * 카메라 화면을 벗어난 후 GPU 발열 방지용
 */
export async function releaseDocAligner() {
  cancelDocAlignerRelease();

  if (session) {
    try {
      await session.release();
    } catch (e) {
      console.warn('Failed to release DocAligner session:', e);
    }
    session = null;
    currentProvider = null;
    console.log('DocAligner session released (GPU memory freed)');
  }

  // 재사용 버퍼도 해제
  realtimeCanvas = null;
  realtimeCtx = null;
  reusableInputBuffer = null;
}

/**
 * 30초 유예 후 세션 해제 예약
 * Camera → Thumbnails → Camera 왕복 시 불필요한 재로드 방지
 */
export function scheduleDocAlignerRelease() {
  cancelDocAlignerRelease();
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    releaseDocAligner();
  }, RELEASE_DELAY);
}

/**
 * 유예 해제 취소 (카메라 화면 복귀 시)
 */
export function cancelDocAlignerRelease() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}

/**
 * 모델 워밍업 (첫 추론 지연 방지)
 * @returns {Promise<number>} 워밍업 추론 시간 (ms)
 */
export async function warmupDocAligner() {
  if (!session) {
    throw new Error('DocAligner model not loaded');
  }

  // 더미 입력 생성 (256x256 검은색 이미지)
  const dummyInput = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
  const inputTensor = new ort.Tensor('float32', dummyInput, [1, 3, INPUT_SIZE, INPUT_SIZE]);

  // 워밍업 추론
  const startTime = performance.now();
  await session.run({ img: inputTensor });
  const warmupTime = performance.now() - startTime;

  console.log(`[DocAligner] Warmup completed: ${warmupTime.toFixed(1)}ms`);
  return warmupTime;
}

/**
 * 이미지 전처리: dataURL → Float32Array (1, 3, 256, 256)
 * @param {ImageData} imageData
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @returns {Float32Array}
 */
function preprocess(imageData, originalWidth, originalHeight) {
  // 256x256 캔버스에 리사이즈
  const canvas = document.createElement('canvas');
  canvas.width = INPUT_SIZE;
  canvas.height = INPUT_SIZE;
  const ctx = canvas.getContext('2d');

  // 원본 이미지를 256x256으로 리사이즈
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = originalWidth;
  tempCanvas.height = originalHeight;
  const tempCtx = tempCanvas.getContext('2d');
  tempCtx.putImageData(imageData, 0, 0);

  ctx.drawImage(tempCanvas, 0, 0, INPUT_SIZE, INPUT_SIZE);
  const resizedData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);

  // Float32Array로 변환 (CHW 형식, 0~1 정규화)
  const float32Data = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
  const data = resizedData.data;

  for (let y = 0; y < INPUT_SIZE; y++) {
    for (let x = 0; x < INPUT_SIZE; x++) {
      const srcIdx = (y * INPUT_SIZE + x) * 4;
      const r = data[srcIdx] / 255.0;
      const g = data[srcIdx + 1] / 255.0;
      const b = data[srcIdx + 2] / 255.0;

      // CHW 형식: [C, H, W]
      float32Data[0 * INPUT_SIZE * INPUT_SIZE + y * INPUT_SIZE + x] = r;
      float32Data[1 * INPUT_SIZE * INPUT_SIZE + y * INPUT_SIZE + x] = g;
      float32Data[2 * INPUT_SIZE * INPUT_SIZE + y * INPUT_SIZE + x] = b;
    }
  }

  return float32Data;
}

/**
 * 후처리: heatmap (1, 4, 128, 128) → 코너 좌표
 * @param {Float32Array} heatmapData
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @returns {{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}} | null}
 */
function postprocess(heatmapData, originalWidth, originalHeight) {
  const corners = [];
  const channelSize = OUTPUT_SIZE * OUTPUT_SIZE;

  // 각 채널(TL, TR, BR, BL)에서 최대값 위치 찾기
  for (let c = 0; c < 4; c++) {
    let maxVal = -Infinity;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < OUTPUT_SIZE; y++) {
      for (let x = 0; x < OUTPUT_SIZE; x++) {
        const idx = c * channelSize + y * OUTPUT_SIZE + x;
        const val = heatmapData[idx];
        if (val > maxVal) {
          maxVal = val;
          maxX = x;
          maxY = y;
        }
      }
    }

    // confidence 체크
    if (maxVal < CONFIDENCE_THRESHOLD) {
      console.warn(`DocAligner: corner ${c} confidence too low: ${maxVal}`);
      return null;
    }

    // 원본 이미지 좌표로 스케일링
    corners.push({
      x: Math.round(maxX * (originalWidth / OUTPUT_SIZE)),
      y: Math.round(maxY * (originalHeight / OUTPUT_SIZE))
    });
  }

  // TL, TR, BR, BL 순서
  return {
    tl: corners[0],
    tr: corners[1],
    br: corners[2],
    bl: corners[3]
  };
}

/**
 * DocAligner로 코너 감지
 * @param {string} imageDataURL
 * @returns {Promise<{tl: {x,y}, tr: {x,y}, br: {x,y}, bl: {x,y}} | null>}
 */
export async function detectCornersWithDocAligner(imageDataURL) {
  if (!session) {
    throw new Error('DocAligner model not loaded');
  }

  // 이미지 로드
  const img = await loadImage(imageDataURL);
  const originalWidth = img.width;
  const originalHeight = img.height;

  // 캔버스에 그리기
  const canvas = document.createElement('canvas');
  canvas.width = originalWidth;
  canvas.height = originalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, originalWidth, originalHeight);

  // 전처리
  const inputData = preprocess(imageData, originalWidth, originalHeight);
  const inputTensor = new ort.Tensor('float32', inputData, [1, 3, INPUT_SIZE, INPUT_SIZE]);

  // 추론
  const startTime = performance.now();
  const results = await session.run({ img: inputTensor });
  const inferenceTime = performance.now() - startTime;

  // 후처리
  const heatmap = results.heatmap;
  const corners = postprocess(heatmap.data, originalWidth, originalHeight);

  return corners;
}

/**
 * 이미지 로드 헬퍼
 */
function loadImage(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = dataURL;
  });
}

/**
 * 비디오 프레임에서 직접 전처리 (dataURL 변환 없이 빠른 처리)
 * 버퍼 재사용으로 GC 부담 최소화
 * @param {HTMLVideoElement} videoElement
 * @param {number} videoWidth
 * @param {number} videoHeight
 * @returns {Float32Array}
 */
function preprocessVideoFrame(videoElement, videoWidth, videoHeight) {
  // 재사용 가능한 캔버스 초기화
  if (!realtimeCanvas) {
    realtimeCanvas = document.createElement('canvas');
    realtimeCanvas.width = INPUT_SIZE;
    realtimeCanvas.height = INPUT_SIZE;
    realtimeCtx = realtimeCanvas.getContext('2d', {
      willReadFrequently: true,
      alpha: false  // 알파 채널 불필요 - 성능 향상
    });
  }

  // Float32Array 버퍼 재사용 (매 프레임 할당 방지)
  if (!reusableInputBuffer) {
    reusableInputBuffer = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
  }

  // 비디오 프레임을 256x256으로 직접 리사이즈
  realtimeCtx.drawImage(videoElement, 0, 0, videoWidth, videoHeight, 0, 0, INPUT_SIZE, INPUT_SIZE);
  const imageData = realtimeCtx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
  const data = imageData.data;

  // 최적화된 픽셀 변환 (CHW 형식, 0~1 정규화)
  const pixelCount = INPUT_SIZE * INPUT_SIZE;
  const rOffset = 0;
  const gOffset = pixelCount;
  const bOffset = pixelCount * 2;
  const inv255 = 1.0 / 255.0;

  for (let i = 0, srcIdx = 0; i < pixelCount; i++, srcIdx += 4) {
    reusableInputBuffer[rOffset + i] = data[srcIdx] * inv255;
    reusableInputBuffer[gOffset + i] = data[srcIdx + 1] * inv255;
    reusableInputBuffer[bOffset + i] = data[srcIdx + 2] * inv255;
  }

  return reusableInputBuffer;
}

/**
 * 후처리 (신뢰도도 함께 반환)
 * @param {Float32Array} heatmapData
 * @param {number} originalWidth
 * @param {number} originalHeight
 * @returns {{corners: Object|null, confidence: number, minConfidence: number}}
 */
function postprocessWithConfidence(heatmapData, originalWidth, originalHeight) {
  const corners = [];
  const channelSize = OUTPUT_SIZE * OUTPUT_SIZE;
  const confidences = [];

  // 각 채널(TL, TR, BR, BL)에서 최대값 위치 찾기
  for (let c = 0; c < 4; c++) {
    let maxVal = -Infinity;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < OUTPUT_SIZE; y++) {
      for (let x = 0; x < OUTPUT_SIZE; x++) {
        const idx = c * channelSize + y * OUTPUT_SIZE + x;
        const val = heatmapData[idx];
        if (val > maxVal) {
          maxVal = val;
          maxX = x;
          maxY = y;
        }
      }
    }

    confidences.push(maxVal);

    // 원본 이미지 좌표로 스케일링
    corners.push({
      x: Math.round(maxX * (originalWidth / OUTPUT_SIZE)),
      y: Math.round(maxY * (originalHeight / OUTPUT_SIZE))
    });
  }

  const minConfidence = Math.min(...confidences);
  const avgConfidence = confidences.reduce((a, b) => a + b, 0) / confidences.length;

  // confidence 체크
  if (minConfidence < CONFIDENCE_THRESHOLD) {
    return { corners: null, confidence: avgConfidence, minConfidence };
  }

  // TL, TR, BR, BL 순서
  return {
    corners: {
      tl: corners[0],
      tr: corners[1],
      br: corners[2],
      bl: corners[3]
    },
    confidence: avgConfidence,
    minConfidence
  };
}

/**
 * 비디오 프레임에서 직접 코너 감지 (실시간용)
 * dataURL 변환 없이 빠르게 처리
 * @param {HTMLVideoElement} videoElement
 * @returns {Promise<{corners: Object|null, confidence: number, inferenceTime: number}>}
 */
export async function detectCornersFromVideo(videoElement) {
  if (!session) {
    throw new Error('DocAligner model not loaded');
  }

  const videoWidth = videoElement.videoWidth;
  const videoHeight = videoElement.videoHeight;

  if (!videoWidth || !videoHeight) {
    return { corners: null, confidence: 0, inferenceTime: 0 };
  }

  // 전처리 (비디오 프레임 직접 사용)
  const inputData = preprocessVideoFrame(videoElement, videoWidth, videoHeight);
  const inputTensor = new ort.Tensor('float32', inputData, [1, 3, INPUT_SIZE, INPUT_SIZE]);

  // 추론
  const startTime = performance.now();
  const results = await session.run({ img: inputTensor });
  const inferenceTime = performance.now() - startTime;

  // 통계 기록
  recordInferenceTime(inferenceTime);

  // 후처리
  const heatmap = results.heatmap;
  const { corners, confidence, minConfidence } = postprocessWithConfidence(heatmap.data, videoWidth, videoHeight);

  return { corners, confidence, minConfidence, inferenceTime };
}
